import { Helmet } from 'react-helmet-async';
import { locales, type Locale } from '@/lib/i18n/config';
import { readSiteUrl } from '@/lib/supabase/env';

// Per-route metadata: title, description, canonical, hreflang alternates and a
// full Open Graph + Twitter Card set so every page yields a rich social preview.
// react-helmet-async swaps the data-rh tags baked into index.html (see that file)
// instead of duplicating them.

const SITE_NAME = 'VPS Laboratorio';
const DEFAULT_IMAGE = '/banners/banner-web-1.jpg';

const ogLocale: Record<Locale, string> = {
  es: 'es_ES',
  pt: 'pt_BR',
  en: 'en_US',
};

const hreflang: Record<Locale, string> = {
  es: 'es-ES',
  pt: 'pt-BR',
  en: 'en-US',
};

type SeoProps = {
  locale: Locale;
  /** Page title without the brand suffix. */
  title: string;
  description: string;
  /** Route path without the locale prefix, e.g. '/areas' or '/noticias/x'. Empty for home. */
  path?: string;
  /** Root-relative or absolute share image. Falls back to the institutional banner. */
  image?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
};

export function Seo({
  locale,
  title,
  description,
  path = '',
  image,
  type = 'website',
  publishedTime,
  modifiedTime,
}: SeoProps) {
  const siteUrl = readSiteUrl().replace(/\/$/, '');
  const cleanPath = path && !path.startsWith('/') ? `/${path}` : path;
  const url = `${siteUrl}/${locale}${cleanPath}`;
  const fullTitle = `${title} | ${SITE_NAME}`;
  const rawImage = image || DEFAULT_IMAGE;
  const imageUrl = rawImage.startsWith('http') ? rawImage : `${siteUrl}${rawImage}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />

      {locales.map((l) => (
        <link
          key={l}
          rel="alternate"
          hrefLang={hreflang[l]}
          href={`${siteUrl}/${l}${cleanPath}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}/es${cleanPath}`} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content={ogLocale[locale]} />
      {type === 'article' && publishedTime ? (
        <meta property="article:published_time" content={publishedTime} />
      ) : null}
      {type === 'article' && modifiedTime ? (
        <meta property="article:modified_time" content={modifiedTime} />
      ) : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
}
